import {
  Box,
  IconButton,
  useTheme,
  Typography,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import { useContext, useState, useEffect } from "react";
import { ColorModeContext, tokens } from "../../../theme";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useNavigate } from "react-router-dom";

const SuperAdminTopbar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const user = localStorage.getItem("superAdminUser");
    if (user) {
      setUserInfo(JSON.parse(user));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("superAdminToken");
    localStorage.removeItem("superAdminUser");
    navigate("/super-admin/auth/secure-access/login");
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 999,
        width: "100%",
        maxWidth: "100%",
        backgroundColor: colors.primary[400],
        backgroundImage: `linear-gradient(90deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
        borderBottom: `1px solid ${colors.grey[800]}30`,
        boxShadow: `0 4px 24px rgba(0, 0, 0, 0.1)`,
        backdropFilter: "blur(10px)",
        mb: 2,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          width: "100%",
        }}
      >
        {/* Left Side - Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1,
              borderRadius: "12px",
              background: `linear-gradient(135deg, ${colors.blueAccent[800]}30 0%, ${colors.greenAccent[800]}20 100%)`,
              border: `1px solid ${colors.blueAccent[700]}40`,
            }}
          >
            <AdminPanelSettingsIcon
              sx={{
                color: colors.blueAccent[300],
                fontSize: "28px",
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${colors.grey[100]} 0%, ${colors.blueAccent[200]} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "0.5px",
              }}
            >
              Super Admin Panel
            </Typography>
          </Box>
        </Box>

        {/* Right Side - Actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Theme Toggle */}
          <Tooltip title={theme.palette.mode === "dark" ? "Light Mode" : "Dark Mode"}>
            <IconButton
              onClick={colorMode.toggleColorMode}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: `${colors.blueAccent[700]}30`,
                border: `1px solid ${colors.blueAccent[500]}40`,
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: `${colors.blueAccent[600]}50`,
                  transform: "scale(1.1)",
                },
              }}
            >
              {theme.palette.mode === "dark" ? (
                <LightModeOutlinedIcon sx={{ color: colors.blueAccent[300] }} />
              ) : (
                <DarkModeOutlinedIcon sx={{ color: colors.blueAccent[300] }} />
              )}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton
              sx={{
                width: 40,
                height: 40,
                backgroundColor: `${colors.blueAccent[700]}30`,
                border: `1px solid ${colors.blueAccent[500]}40`,
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: `${colors.blueAccent[600]}50`,
                  transform: "scale(1.1)",
                },
              }}
            >
              <NotificationsOutlinedIcon sx={{ color: colors.blueAccent[300] }} />
            </IconButton>
          </Tooltip>

          {/* User Menu */}
          <Tooltip title="Account">
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: `${colors.blueAccent[700]}30`,
                border: `1px solid ${colors.blueAccent[500]}40`,
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: `${colors.blueAccent[600]}50`,
                  transform: "scale(1.1)",
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
                }}
              >
                <AdminPanelSettingsIcon sx={{ fontSize: "18px" }} />
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* User Menu Dropdown */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[800]}30`,
                borderRadius: "12px",
                boxShadow: `0 8px 32px rgba(0, 0, 0, 0.2)`,
              },
            }}
          >
            {userInfo && (
              <MenuItem
                disabled
                sx={{
                  py: 1.5,
                  px: 2,
                  "&.Mui-disabled": {
                    opacity: 1,
                  },
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: colors.grey[100],
                    }}
                  >
                    {userInfo.email || "Super Admin"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.grey[400],
                      fontSize: "0.7rem",
                    }}
                  >
                    Administrator
                  </Typography>
                </Box>
              </MenuItem>
            )}
            <Divider sx={{ borderColor: colors.grey[800] }} />
            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1.5,
                px: 2,
                color: colors.redAccent[400],
                "&:hover": {
                  backgroundColor: `${colors.redAccent[800]}20`,
                },
              }}
            >
              <LogoutIcon sx={{ mr: 1.5, fontSize: "20px" }} />
              <Typography variant="body2">Logout</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Box>
  );
};

export default SuperAdminTopbar;

